import { Box } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { TypoCaption } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { isValidEmail, normalizeEmail } from "src/utils/validation";
import { FormParser } from "src/view/FormParser";
import { StartScreenView } from "../StartScreenView";
import { DS } from "../ds/Kit";
import {
	AUTH_FORM_ACTIONS_SX,
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";
import { S_AUTH_CHANGE_VIEW } from "./AuthView";

const getForm = () => ({
	email: "",
	$__email_type: "email",
	$__email_label: "Email address",
});

export const ForgotPassword = () => {
	const [busy, setBusy] = useState(false);
	const [completed, setCompleted] = useState<boolean>();
	const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		if (!resendAvailableAt) return;

		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, [resendAvailableAt]);

	const resendSecondsLeft = resendAvailableAt ? Math.max(0, Math.ceil((resendAvailableAt - now) / 1000)) : 0;
	const canSubmit = !resendAvailableAt || resendSecondsLeft <= 0;

	return (
		<StartScreenView busy={busy}>
			<AuthCard
				icon={completed ? "mail" : undefined}
				eyebrow="Account recovery"
				title={completed ? "Check your email" : "Reset your password"}
				subtitle={
					completed
						? "If the address belongs to an account, password reset instructions are on the way."
						: "Enter the email address associated with your Cardpartner account."
				}>
				{!completed ? (
					<FormParser
						key="restoreForm"
						ignoreObserver={true}
						fullSizeButtons={true}
						actionsSx={AUTH_FORM_ACTIONS_SX}
						secondaryButton={null}
						primaryButton={{ title: "Send reset link", variant: "primary", sx: { ...AUTH_PRIMARY_BUTTON_SX, flexGrow: 1 } }}
						onSubmit={async (data: ReturnType<typeof getForm>): Promise<any> => {
							const email = normalizeEmail(data.email);
							if (!isValidEmail(email)) {
								return { data, error: { code: 0, message: "Enter a valid email address." } };
							}
							if (!canSubmit) {
								return {
									data,
									error: { code: 0, message: `Please wait ${resendSecondsLeft}s before requesting another email.` },
								};
							}

							setBusy(true);
							let response: IAPIResponse;
							try {
								response = await GD.REQ_API.request({
									method: "users/password/recover",
									data: { email },
								});
							} finally {
								setBusy(false);
							}
							if (response.error) return { data, error: response.error };
							const result = (response.data as any)?.data ?? response.data;
							if (result?.status === true || result === true) {
								setCompleted(true);
								setResendAvailableAt(Date.now() + 60_000);
							}
							return { data };
						}}
						onRequest={async () => getForm()}
					/>
				) : (
					<>
						<AuthStatus tone="success" title="Reset instructions sent">
							Check your inbox and follow the secure link to choose a new password.
						</AuthStatus>
						{resendSecondsLeft > 0 ? (
							<TypoCaption sx={{ marginTop: "12px", color: DS.muted, textAlign: "center", lineHeight: 1.5 }}>
								Another request will be available in {resendSecondsLeft}s.
							</TypoCaption>
						) : null}
					</>
				)}

				<AuthDivider />
				<Box>
					<Button
						title="Back to sign in"
						variant={completed ? "primary" : "secondary"}
						onClick={() => S_AUTH_CHANGE_VIEW.invoke("login")}
						sx={completed ? AUTH_PRIMARY_BUTTON_SX : AUTH_SECONDARY_BUTTON_SX}
					/>
				</Box>
			</AuthCard>
		</StartScreenView>
	);
};
